import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { FormModal } from '@/components/ui/form-modal'
import { NumberFieldControl, TextFieldControl } from '@/components/ui/form-fields'
import { toTranslation } from '@/lib/api/common'

import { useBranchActions } from '../hooks'
import type { SaveBranchBody, TenantBranch } from '../types'

/** `HH:mm`, the only form the API and the storefront both read. */
const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/

const schema = z.object({
  nameEn: z.string().trim().min(1, 'Give the branch a name.'),
  nameKm: z.string().trim().optional(),
  addressEn: z.string().trim().optional(),
  addressKm: z.string().trim().optional(),
  // Required on the server — a branch without coordinates cannot be placed on a
  // map or costed for delivery — so the form insists rather than sending zeros,
  // which would put every new outlet in the Gulf of Guinea.
  latitude: z.number({ message: 'Latitude is required.' }).min(-90).max(90),
  longitude: z.number({ message: 'Longitude is required.' }).min(-180).max(180),
  openHour: z.union([z.literal(''), z.string().regex(timePattern, 'Use HH:mm, e.g. 08:00')]),
  closeHour: z.union([z.literal(''), z.string().regex(timePattern, 'Use HH:mm, e.g. 22:00')]),
})

type FormValues = z.infer<typeof schema>

interface BranchFormDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  /** The branch being edited, or `null` to open the dialog on a new one. */
  branch: TenantBranch | null
  onSaved: () => void
}

export function BranchFormDialog({ isOpen, onOpenChange, branch, onSaved }: BranchFormDialogProps) {
  const { save } = useBranchActions()

  const { control, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      nameEn: branch?.name?.en ?? '',
      nameKm: branch?.name?.km ?? '',
      addressEn: branch?.address?.en ?? '',
      addressKm: branch?.address?.km ?? '',
      latitude: branch?.latitude,
      longitude: branch?.longitude,
      openHour: branch?.openHour ?? '',
      closeHour: branch?.closeHour ?? '',
    },
  })

  const submit = handleSubmit(async (values) => {
    const body: SaveBranchBody = {
      name: toTranslation(values.nameEn, values.nameKm),
      address: toTranslation(values.addressEn ?? '', values.addressKm),
      latitude: values.latitude,
      longitude: values.longitude,
      openHour: values.openHour || undefined,
      closeHour: values.closeHour || undefined,
    }

    await save.mutateAsync({ id: branch?.id ?? null, body })
    onSaved()
  })

  return (
    <FormModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={branch ? 'Edit branch' : 'New branch'}
      description="An outlet. Products, staff, tables and opening hours all hang off a branch."
      submitLabel={branch ? 'Save changes' : 'Create branch'}
      onSubmit={submit}
      isBusy={save.isPending}
      error={save.error}
      errorFallback="Could not save this branch."
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextFieldControl
          control={control}
          name="nameEn"
          label="Name (English)"
          isRequired
          autoFocus
        />
        <TextFieldControl control={control} name="nameKm" label="Name (Khmer)" />
        <TextFieldControl control={control} name="addressEn" label="Address (English)" />
        <TextFieldControl control={control} name="addressKm" label="Address (Khmer)" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <NumberFieldControl
          control={control}
          name="latitude"
          label="Latitude"
          minValue={-90}
          maxValue={90}
          step={0.000001}
          formatOptions={{ maximumFractionDigits: 6, useGrouping: false }}
          isRequired
        />
        <NumberFieldControl
          control={control}
          name="longitude"
          label="Longitude"
          minValue={-180}
          maxValue={180}
          step={0.000001}
          formatOptions={{ maximumFractionDigits: 6, useGrouping: false }}
          isRequired
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <TextFieldControl
          control={control}
          name="openHour"
          label="Opens"
          placeholder="08:00"
          description="Leave blank to fall back to the weekly hours."
        />
        <TextFieldControl control={control} name="closeHour" label="Closes" placeholder="22:00" />
      </div>
    </FormModal>
  )
}
